import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/public/Brand";
import { ThemeToggle } from "@/components/public/PublicLayout";

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | verify
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const startRegister = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { name, email, password });
      setStep("verify");
      toast.success("Verification code sent to your email");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-registration", { email, code: otp });
      login(r.data.token, r.data.user);
      toast.success("Account verified! Welcome to MANHA DIGITAL.");
      navigate("/portal");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-secondary/40 p-6">
      <div className="mb-6 flex w-full max-w-sm items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Brand height={38} textClass="font-display text-lg font-extrabold" />
        </Link>
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
        <h1 className="font-display text-2xl font-bold">Create Account</h1>
        <p className="mt-1 text-sm text-muted-foreground">Join MANHA DIGITAL and start shopping on installments.</p>

        {step === "form" ? (
          <form onSubmit={startRegister} className="mt-6 grid gap-4">
            <div><Label>Full Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" /></div>
            <div><Label>Password</Label><Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" /></div>
            <div><Label>Confirm Password</Label><Input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="signup-confirm" /></div>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="signup-submit">{busy ? "Sending code..." : "Create Account"}</Button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 grid gap-4">
            <p className="text-sm text-muted-foreground">We sent a 6-digit verification code to <span className="font-semibold text-foreground">{email}</span>.</p>
            <div><Label>Verification Code</Label><Input required value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} placeholder="000000" data-testid="signup-otp" /></div>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="signup-verify">{busy ? "Verifying..." : "Verify & Continue"}</Button>
            <button type="button" onClick={() => setStep("form")} className="text-xs text-muted-foreground hover:text-primary">← Edit details</button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link></p>
      </div>
    </div>
  );
}
